/**
 * Language / country switcher: header button, overlay, cookies + triggers (expedition port).
 */

const getThemeUri = () => document.documentElement.getAttribute('data-theme-uri') || '';

const getCookie = (name) => {
	const row = document.cookie
		.split(';')
		.map((c) => c.trim())
		.find((c) => c.startsWith(name + '='));
	return row ? row.split('=')[1] : null;
};

const setCookie = (name, value, days = 365) => {
	const date = new Date();
	date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
	const expires = `expires=${date.toUTCString()}`;
	document.cookie = `${name}=${value}; ${expires}; path=/; secure; samesite=Lax`;
};

const getFlagUrl = (countryOrLanguage) => {
	const code = (countryOrLanguage || '').toLowerCase();
	const baseUrl = '/wp-content/plugins/sitepress-multilingual-cms/res/flags/';
	const themeUri = getThemeUri();
	const flagMap = {
		nl: baseUrl + 'nl.png',
		netherlands: baseUrl + 'nl.png',
		de: baseUrl + 'de.png',
		germany: baseUrl + 'de.png',
		ch: baseUrl + 'ch.png',
		switzerland: baseUrl + 'ch.png',
		es: baseUrl + 'es.png',
		spain: baseUrl + 'es.png',
		en: baseUrl + 'en.png',
		uk: baseUrl + 'en.png',
		fr: baseUrl + 'fr.png',
		france: baseUrl + 'fr.png',
		it: baseUrl + 'it.png',
		italy: baseUrl + 'it.png',
		usa: baseUrl + 'us.png',
		us: baseUrl + 'us.png',
		'united states of america': baseUrl + 'us.png',
		'united states': baseUrl + 'us.png',
		apac: themeUri ? `${themeUri}/assets/img/svg/apac.svg` : '',
		'asia-pacific': themeUri ? `${themeUri}/assets/img/svg/apac.svg` : '',
		global: null,
	};
	return flagMap[code] || null;
};

const closeMegaMenus = () => {
	document.dispatchEvent(new CustomEvent('close-mobile-navigation'));
	const openToggles = document.querySelectorAll('.has-megamenu > .dropdown-toggle.show, .dropdown-toggle.show');
	openToggles.forEach((toggle) => {
		toggle.classList.remove('show');
		toggle.setAttribute('aria-expanded', 'false');
		const nextSibling = toggle.nextElementSibling;
		if (nextSibling && nextSibling.classList.contains('dropdown-menu')) {
			nextSibling.classList.remove('show');
		}
	});
	document.querySelectorAll('.dropdown-menu.megamenu.show, .megamenu.show').forEach((menu) => {
		menu.classList.remove('show');
	});
	document.querySelectorAll('[data-menu-dropdown-icon].rotate-180').forEach((icon) => {
		icon.classList.remove('rotate-180');
	});
};

const initClose = () => {
	const body = document.querySelector('body');
	const overlay = document.querySelector('.js-language-switcher-overlay');
	const toggleButtons = document.querySelectorAll('.js-language-switcher-toggle.is-active');
	const closeButtons = document.querySelectorAll('.js-language-switcher-close.is-active');
	if (!overlay) {
		return;
	}
	toggleButtons.forEach((button) => {
		button.classList.remove('is-active');
		button.setAttribute('aria-expanded', 'false');
	});
	closeButtons.forEach((button) => {
		button.classList.remove('is-active');
	});
	overlay.classList.remove('is-active');
	overlay.setAttribute('aria-hidden', 'true');
	if (body) {
		body.classList.remove('overflow-hidden');
	}
};

const initOverlayOpen = () => {
	const body = document.querySelector('body');
	const overlay = document.querySelector('.js-language-switcher-overlay');
	if (!overlay) {
		return;
	}

	closeMegaMenus();

	if (overlay.classList.contains('is-active')) {
		initClose();
	} else {
		overlay.classList.add('is-active');
		overlay.setAttribute('aria-hidden', 'false');
		if (body) {
			body.classList.add('overflow-hidden');
		}
	}
};

const initLanguageSwitcher = () => {
	const toggleButtons = document.querySelectorAll('.js-language-switcher-toggle');
	const closeButtons = document.querySelectorAll('.js-language-switcher-close');
	const overlay = document.querySelector('.js-language-switcher-overlay');
	if (overlay && !overlay.classList.contains('is-active')) {
		overlay.setAttribute('aria-hidden', 'true');
	}
	if (!toggleButtons.length) {
		return;
	}

	document.addEventListener('click', (event) => {
		const overlay = document.querySelector('.js-language-switcher-overlay');
		const overlayInner = overlay ? overlay.querySelector('.language-switcher__overlay-inner') : null;
		if (!overlay || !overlay.classList.contains('is-active')) {
			return;
		}
		const clickedToggle = event.target.closest('.js-language-switcher-toggle');
		const clickedClose = event.target.closest('.js-language-switcher-close');
		const clickedInsidePanel = overlayInner ? overlayInner.contains(event.target) : false;
		if (!clickedInsidePanel && !clickedToggle && !clickedClose) {
			initClose();
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			initClose();
		}
	});

	toggleButtons.forEach((button) => {
		button.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();

			const overlay = document.querySelector('.js-language-switcher-overlay');
			if (overlay && overlay.classList.contains('is-active')) {
				initClose();
				return;
			}
			toggleButtons.forEach((toggleButton) => {
				toggleButton.classList.add('is-active');
				toggleButton.setAttribute('aria-expanded', 'true');
			});
			closeButtons.forEach((closeButton) => {
				closeButton.classList.add('is-active');
			});
			initOverlayOpen();
		});
	});

	closeButtons.forEach((button) => {
		button.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			initClose();
		});
	});
};

document.addEventListener('close-language-switcher', () => {
	initClose();
});

const initLanguageSwitcherTriggers = () => {
	const triggers = document.querySelectorAll('.js-language-switcher-trigger');
	triggers.forEach((trigger) => {
		trigger.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();

			const country = trigger.dataset.country || 'global';
			const language = trigger.dataset.language || 'en';
			const clickedUrl = trigger.href || window.location.href;
			const flagUrl = getFlagUrl(country);

			setCookie('era_country_pref', country);
			setCookie('wp-wpml_current_language', language);
			if (flagUrl) {
				setCookie('eraneos_flag_url', flagUrl);
			} else {
				setCookie('eraneos_flag_url', 'global');
			}

			try {
				document.dispatchEvent(new CustomEvent('countryPreferenceChanged', { detail: { country, language, flagUrl } }));
			} catch (_) {
				// noop
			}

			setTimeout(() => {
				window.location.href = clickedUrl;
			}, 100);
		});
	});
};

const setFlagContainersFromUrl = () => {
	try {
		const urlParams = new URLSearchParams(window.location.search);
		const countryParam = urlParams.get('country');
		if (!countryParam) {
			return;
		}
		const normalized = countryParam.toLowerCase().trim();
		const countryMap = {
			netherlands: 'nl',
			germany: 'de',
			switzerland: 'ch',
			spain: 'es',
			france: 'fr',
			italy: 'it',
			us: 'us',
			usa: 'us',
			'united states of america': 'us',
			'united states': 'us',
			'asia-pacific': 'apac',
		};
		const shortCode = countryMap[normalized] || normalized;
		const validCountries = ['nl', 'global', 'de', 'es', 'ch', 'fr', 'it', 'uk', 'us', 'usa', 'apac'];
		const validated = validCountries.includes(shortCode) ? shortCode : 'global';
		setCookie('era_country_pref', validated);
		const flagUrl = getFlagUrl(validated);
		if (flagUrl) {
			setCookie('eraneos_flag_url', flagUrl);
		} else {
			setCookie('eraneos_flag_url', 'global');
		}

		const flagContainers = document.querySelectorAll('.language-switcher__button-flag');
		flagContainers.forEach((flagContainer) => {
			if (validated === 'global' || !flagUrl) {
				flagContainer.innerHTML = '<i class="fa-light fa-globe" aria-hidden="true"></i>';
			} else {
				flagContainer.innerHTML = `<img class="language-switcher__button-flag-image size-[18px] object-cover" src="${flagUrl}" alt="" width="18" height="18" loading="lazy">`;
			}
		});
	} catch (_) {
		// noop
	}
};

const setInitialFlagFromCookie = () => {
	try {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get('country')) {
			return;
		}
		const flagUrlCookie = getCookie('eraneos_flag_url');
		if (!flagUrlCookie) {
			return;
		}
		const decoded = decodeURIComponent(flagUrlCookie);
		const flagContainers = document.querySelectorAll('.language-switcher__button-flag');
		flagContainers.forEach((flagContainer) => {
			if (decoded === 'global') {
				flagContainer.innerHTML = '<i class="fa-light fa-globe" aria-hidden="true"></i>';
			} else {
				flagContainer.innerHTML = `<img class="language-switcher__button-flag-image size-[18px] object-cover" src="${decoded}" alt="" width="18" height="18" loading="lazy">`;
			}
		});
	} catch (_) {
		// noop
	}
};

document.addEventListener('countryPreferenceChanged', (event) => {
	try {
		const detail = event.detail || {};
		const newCountry = detail.country || null;
		const newLanguage = detail.language || null;
		let newFlagUrl = detail.flagUrl || null;
		if (!newFlagUrl && newCountry) {
			newFlagUrl = getFlagUrl(newCountry);
		}
		const flagContainers = document.querySelectorAll('.language-switcher__button-flag');
		flagContainers.forEach((flagContainer) => {
			if (newFlagUrl) {
				flagContainer.innerHTML = `<img class="language-switcher__button-flag-image size-[18px] object-cover" src="${newFlagUrl}" alt="" width="18" height="18" loading="lazy">`;
			} else if (newCountry === 'global') {
				flagContainer.innerHTML = '<i class="fa-light fa-globe" aria-hidden="true"></i>';
			}
		});
		if (newLanguage) {
			const display = newLanguage === 'ch' ? 'DE' : newLanguage;
			document.querySelectorAll('.language-switcher__button-text').forEach((node) => {
				node.textContent = display;
			});
		}
	} catch (_) {
		// noop
	}
});

document.addEventListener('DOMContentLoaded', () => {
	initLanguageSwitcher();
	initLanguageSwitcherTriggers();
	setFlagContainersFromUrl();
	setInitialFlagFromCookie();
});
